<script setup lang="ts">
import { computed, inject, type Component } from 'vue';
import Plus from 'lucide-vue-next/dist/esm/icons/plus.js';
import NxButton from '../Button/NxButton.vue';
import NxSearchInput from '../Inputs/NxSearchInput.vue';
import { NxSidebarEmbeddedKey } from './embedded';
import NxAppBrand from './NxAppBrand.vue';
import { nxWorkspaceRegionKey } from '../Workspace/context';

interface Brand {
  /** App name shown next to the logo */
  name: string;
  /** Optional caption under the name (workspace, tagline, …) */
  sub?: string;
  /** Signed-in account or mailbox under the name, in its own spelling (see NxAppBrand). */
  account?: string;
  /** Logo: either a 1-2 char string (rendered in the gradient tile) or a Component */
  logo: string | Component;
}

/**
 * The sidebar's search: a real field that filters what the sidebar lists
 * (bind `v-model:query`). It never opens the command palette and shows no
 * shortcut — the palette has its own key.
 */
interface SearchConfig {
  /** Defaults to „Suche“; apps with i18n pass their translation. */
  placeholder?: string;
  /** Accessible name; defaults to the placeholder. */
  label?: string;
}

/**
 * The app's primary create action („Neue E-Mail“, „Neuer Termin“), shown as a
 * full-width button under the brand, at the default height `s`. Pair it with `primaryInSidebar` on
 * NxWorkspaceHeader so the header does not repeat it while the sidebar shows.
 */
export interface NxAppSidebarPrimary {
  label: string;
  /** Leading icon; defaults to a plus. */
  icon?: Component;
  disabled?: boolean;
  /** Tooltip (e.g. why it is disabled). */
  title?: string;
}

interface UserCard {
  name: string;
  initials?: string;
  status?: string;
}

const props = withDefaults(
  defineProps<{
    brand: Brand;
    /** Primary create action under the brand; emits `primary`. The `#primary` slot replaces it. */
    primary?: NxAppSidebarPrimary;
    search?: SearchConfig;
    user?: UserCard;
    /**
     * Width override in px. Default 272 (NxWorkspace's sidebar default). Ignored when the sidebar fills its parent.
     */
    width?: number;
    /**
     * Fill the parent (height and width) instead of `100vh` × `width`, without an
     * own edge line — the parent sizes and frames it. Defaults to `true` inside
     * the sidebar region of an NxWorkspace, `false` elsewhere.
     */
    fill?: boolean;
  }>(),
  { primary: undefined, search: undefined, user: undefined, width: undefined, fill: undefined }
);

const emit = defineEmits<{
  (e: 'brand-click'): void;
  (e: 'primary'): void;
  (e: 'user-click'): void;
}>();

/** Text of the sidebar search (`v-model:query`); the app filters its list with it. */
const query = defineModel<string>('query', { default: '' });

const workspaceRegion = inject(nxWorkspaceRegionKey, null);
const isFill = computed(() => props.fill ?? workspaceRegion?.region === 'sidebar');

const widthPx = computed(() => (isFill.value ? undefined : `${props.width ?? 272}px`));

/**
 * Set by the Connect shell. When embedded, sub-app sidebars hide their
 * footer (user-card / UserMenu) — the shell already renders a global
 * account menu in its left rail.
 */
const isEmbedded = inject(NxSidebarEmbeddedKey, false);
</script>

<template>
  <aside
    class="cn-sidebar"
    :class="{ 'cn-sidebar--fill': isFill }"
    data-nx-scope="sidebar"
    :style="{ width: widthPx }"
  >
    <slot name="brand" :brand="brand">
      <NxAppBrand :brand="brand" @click="emit('brand-click')" />
    </slot>

    <!-- The primary action stays visible when embedded in Connect. -->
    <div v-if="$slots.primary || primary" class="cn-sidebar-primary">
      <slot name="primary">
        <NxButton
          v-if="primary"
          width="full"
          height="s"
          :disabled="primary.disabled"
          :title="primary.title"
          data-testid="nx-app-sidebar-primary"
          @click="emit('primary')"
        >
          <component :is="primary.icon ?? Plus" class="nxt-icon-sm" aria-hidden="true" />
          <span class="cn-sidebar-primary-label">{{ primary.label }}</span>
        </NxButton>
      </slot>
    </div>

    <div v-if="search" class="cn-sidebar-search" @keydown.escape="query = ''">
      <NxSearchInput
        v-model="query"
        height="s"
        :placeholder="search.placeholder"
        :aria-label="search.label ?? search.placeholder ?? 'Suche'"
      />
    </div>

    <slot name="header-extra" />

    <div class="cn-sidebar-nav">
      <slot />
    </div>

    <!-- Stays visible when embedded in Connect (a quota meter, a sync state). -->
    <div v-if="$slots.bottom" class="cn-sidebar-bottom">
      <slot name="bottom" />
    </div>

    <div v-if="!isEmbedded" class="cn-sidebar-foot">
      <slot name="footer">
        <div v-if="user" class="cn-sidebar-user" @click="emit('user-click')">
          <div class="cn-sidebar-avatar">
            {{ user.initials || user.name.slice(0, 2).toUpperCase() }}
          </div>
          <div class="cn-sidebar-user-meta">
            <div class="cn-sidebar-user-name">{{ user.name }}</div>
            <div v-if="user.status" class="cn-sidebar-user-status">{{ user.status }}</div>
          </div>
        </div>
      </slot>
    </div>
  </aside>
</template>

<style scoped>
.cn-sidebar {
  background: var(--nxt-surface-bg);
  border-right: var(--nxt-hairline) solid var(--nxt-border);
  display: flex;
  flex-direction: column;
  padding: var(--nxt-space-md) var(--nxt-space-sm);
  gap: var(--nxt-space-sm);
  height: 100vh;
  flex-shrink: 0;
  min-height: 0;
}

.cn-sidebar--fill {
  width: 100%;
  height: 100%;
  border-right: none;
}

.cn-sidebar-primary {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  min-width: 0;
}
.cn-sidebar-primary-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cn-sidebar-bottom {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  border-top: var(--nxt-hairline) solid var(--nxt-border);
  padding-top: var(--nxt-space-xs);
}
/* Slot content that renders nothing (a quota meter without a quota) leaves
   no empty band with a line above it. */
.cn-sidebar-bottom:empty {
  display: none;
}

.cn-sidebar-search {
  flex: 0 0 auto;
  min-width: 0;
}

.cn-sidebar-nav {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-xs);
  overflow-y: auto;
  min-height: 0;
}

.cn-sidebar-foot {
  border-top: var(--nxt-hairline) solid var(--nxt-border);
  padding-top: var(--nxt-space-xs);
  margin-top: var(--nxt-space-2xs);
}

.cn-sidebar-user {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-2xs) var(--nxt-space-xs);
  border-radius: var(--nxt-radius-md);
  cursor: pointer;
  transition: background var(--nxt-motion-fast);
}
.cn-sidebar-user:hover {
  background: var(--nxt-brand-subtle-bg);
}

.cn-sidebar-avatar {
  width: var(--nxt-avatar-size-md);
  height: var(--nxt-avatar-size-md);
  border-radius: var(--nxt-radius-full);
  background: var(--nxt-brand-subtle-bg);
  display: grid;
  place-items: center;
  font-weight: var(--nxt-font-weight-semibold);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg);
  flex-shrink: 0;
}

.cn-sidebar-user-meta {
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.cn-sidebar-user-name {
  font-weight: var(--nxt-font-weight-semibold);
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cn-sidebar-user-status {
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-subtle);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
